import accountantApi from "api/accountant";
import {IAccountant, IUser} from "api/types";
import Modal from "components/Modal";
import {availableCertifications} from "constants/certifications";
import useMaybeNotSetupUser from "contexts/MaybeNotSetupUser/useMaybeNotSetupUser";
import {handleError} from "helpers/errors";
import {FC, useEffect, useRef, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {extractUrl} from "../utils";

const CertificationPopup: FC = () => {
  const {user, setUser} = useMaybeNotSetupUser();
  const isAccountant = user?.type === "accountant";
  const [sp, setSP] = useSearchParams();
  const isCertificationUpdate = sp.get("update") === "certification";
  const name = availableCertifications[Number(sp.get("certification"))];
  const open = isAccountant && isCertificationUpdate && name !== undefined;
  const [certification, setCertification] = useState<string>("");
  const [preview, setPreview] = useState<{imageUrl?: string} | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const previewUrlRef = useRef<string>("");

  const onCancel = () => {
    if (submitting) return;
    setSP((old) => {
      old.delete("update");
      old.delete("certification");
      return old;
    });
  };

  const loadPreview = (url: string) => {
    if (!url || !url.includes("credly.com/badges/")) {
      setPreview(null);
      return;
    }
    if (previewUrlRef.current === url) return;
    previewUrlRef.current = url;
    setPreviewing(true);
    setPreview(null);
    accountantApi
      .loadCertificationData(url)
      .then((data) => {
        if (previewUrlRef.current === url) setPreview(data);
      })
      .finally(() => setPreviewing(false));
  };

  const onEdit = () => {
    if (submitting) return;
    if (!certification) return handleError("Please enter your certification link");
    if (!certification.includes("credly.com/badges/")) return handleError("Enter a valid Credly Link");
    setSubmitting(true);
    const cert = preview?.imageUrl ? {url: certification, imageUrl: preview.imageUrl} : certification;
    accountantApi
      .updateCertification(name, cert)
      .then((updated) => {
        setUser?.(
          (old) =>
            ({
              ...old,
              certifications: {
                ...(old as IAccountant).certifications,
                ...updated,
              },
            }) as IUser,
        );
        setSP((old) => {
          old.delete("update");
          old.delete("certification");
          return old;
        });
      })
      .finally(() => setSubmitting(false));
  };

  useEffect(() => {
    if (open) {
      const url = extractUrl(user?.certifications?.[name] ?? "");
      setCertification(url);
      setPreview(null);
      previewUrlRef.current = "";
    }
  }, [user, open, name]);

  return (
    <Modal open={open} close={!submitting ? onCancel : () => {}}>
      <div className="bg-white rounded-2xl border border-brand-border divide-y divide-brand-border w-full max-w-md">
        <div className="px-5 py-4">
          <h2 className="text-lg font-bold text-brand-navy">{name} Certification</h2>
        </div>
        <div className="flex flex-col px-5 py-4 gap-4">
          <input
            type="text"
            placeholder="Credly badge URL"
            value={certification}
            onChange={(e) => setCertification(e.currentTarget.value)}
            onBlur={() => loadPreview(certification)}
            onKeyDown={(e) => e.key === "Enter" && onEdit()}
          />
          {previewing && <span className="text-xs text-brand-muted">Loading preview…</span>}
          {preview && (
            <div className="flex items-center gap-3">
              {preview.imageUrl ? (
                <img src={preview.imageUrl} alt={name} className="w-14 h-14 rounded-xl object-contain" />
              ) : (
                <span className="text-xs text-brand-muted">No badge image found</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <button className="btn-secondary" disabled={submitting} onClick={!submitting ? onCancel : () => {}}>
            Cancel
          </button>
          <button disabled={submitting} onClick={onEdit} className="btn-primary">
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CertificationPopup;
